// src/components/Contact.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Container, Typography, TextField, Button, Alert, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import logo from '../assets/logo.png';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageBg = styled(Box)(() => ({
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #fffcf5 0%, #ffffff 60%)',
  display: 'flex',
  flexDirection: 'column',
}));

const Card = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid #f0f0f0',
  borderRadius: 24,
  padding: theme.spacing(5),
  boxShadow: '0 20px 50px rgba(0,0,0,0.04)',
  animation: `${fadeIn} 0.5s ease-out`,
  [theme.breakpoints.down('sm')]: { padding: theme.spacing(3) },
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    transition: 'all 0.2s ease',
    '&:hover': { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' },
    '&.Mui-focused': { boxShadow: '0 4px 12px rgba(255, 183, 27, 0.18)' },
  },
}));

const PrimaryButton = styled(Button)(() => ({
  borderRadius: 14,
  textTransform: 'none',
  fontWeight: 700,
  padding: '14px 32px',
  fontSize: '1rem',
  background: 'linear-gradient(135deg, #ffb71b 0%, #ff9e00 100%)',
  color: '#000',
  boxShadow: '0 4px 16px rgba(255, 183, 27, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #ffc94d 0%, #ffb71b 100%)',
    boxShadow: '0 6px 24px rgba(255, 183, 27, 0.4)',
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': { background: '#eee', color: '#aaa', boxShadow: 'none' },
  transition: 'all 0.2s ease',
}));

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function Contact() {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const widgetIdRef = useRef(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Render reCAPTCHA when grecaptcha is available
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (window.grecaptcha && window.grecaptcha.render && recaptchaRef.current && widgetIdRef.current === null) {
        try {
          widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
          });
        } catch (e) {
          // already rendered or other; ignore
        }
      } else {
        setTimeout(tryRender, 250);
      }
    };
    tryRender();
    return () => { cancelled = true; };
  }, []);

  const resetCaptcha = useCallback(() => {
    if (window.grecaptcha && widgetIdRef.current !== null) {
      try { window.grecaptcha.reset(widgetIdRef.current); } catch {}
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in your name, email, and message.');
      return;
    }
    if (!RECAPTCHA_SITE_KEY) {
      setError('Contact form is not configured. Please email info@rewarding-app.com directly.');
      return;
    }
    const recaptchaToken = window.grecaptcha?.getResponse(widgetIdRef.current);
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, message, recaptchaToken }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data?.message || 'Failed to send.');
      setSuccess(true);
      setName(''); setEmail(''); setSubject(''); setMessage('');
      resetCaptcha();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      resetCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageBg>
      {/* Top bar with back-to-home */}
      <Box sx={{ py: 3 }}>
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Box component="img" src={logo} alt="Rewarding" sx={{ height: 36 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#111', letterSpacing: '-0.02em' }}>
              Rewarding
            </Typography>
          </Box>
          <IconButton onClick={() => navigate('/')} sx={{ color: '#666' }} aria-label="Back to home">
            <ArrowBackIcon />
          </IconButton>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pb: 8, flex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            background: '#fff8e7', border: '1px solid #ffe0a0',
            borderRadius: 8, px: 2, py: 0.5, mb: 3,
          }}>
            <EmailIcon sx={{ fontSize: 16, color: '#ffb71b' }} />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#b07800' }}>
              Get in touch
            </Typography>
          </Box>
          <Typography sx={{
            fontWeight: 900, fontSize: { xs: '2.2rem', md: '3rem' }, color: '#111',
            lineHeight: 1.15, mb: 2, letterSpacing: '-0.02em',
          }}>
            Contact Us
          </Typography>
          <Typography sx={{ color: '#777', fontSize: '1.1rem', lineHeight: 1.7, maxWidth: 520, mx: 'auto' }}>
            Questions, feedback, or partnership ideas? We'd love to hear from you. We typically reply within one business day.
          </Typography>
        </Box>

        <Card>
          {success ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
              <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#111', mb: 1 }}>
                Message sent!
              </Typography>
              <Typography sx={{ color: '#777', mb: 4, maxWidth: 400, mx: 'auto', lineHeight: 1.7 }}>
                Thanks for reaching out — we'll get back to you at the email you provided.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button onClick={() => setSuccess(false)} sx={{ textTransform: 'none', color: '#666' }}>
                  Send another
                </Button>
                <PrimaryButton onClick={() => navigate('/')}>
                  Back to home
                </PrimaryButton>
              </Box>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                <StyledTextField
                  label="Your Name"
                  required
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                />
                <StyledTextField
                  label="Email Address"
                  type="email"
                  required
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                />
              </Box>

              <StyledTextField
                label="Subject"
                fullWidth
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                inputProps={{ maxLength: 300 }}
                sx={{ mb: 2.5 }}
              />

              <StyledTextField
                label="Your Message"
                required
                fullWidth
                multiline
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                inputProps={{ maxLength: 5000 }}
                helperText={`${message.length} / 5000`}
                sx={{ mb: 3 }}
              />

              {/* reCAPTCHA */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                {RECAPTCHA_SITE_KEY ? (
                  <Box ref={recaptchaRef} />
                ) : (
                  <Alert severity="warning" sx={{ width: '100%', borderRadius: 2 }}>
                    reCAPTCHA is not configured. Set <code>REACT_APP_RECAPTCHA_SITE_KEY</code> to enable the form.
                  </Alert>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <PrimaryButton
                  type="submit"
                  disabled={submitting}
                  endIcon={!submitting && <SendIcon />}
                >
                  {submitting ? 'Sending…' : 'Send Message'}
                </PrimaryButton>
              </Box>
            </Box>
          )}
        </Card>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography sx={{ color: '#999', fontSize: '0.9rem' }}>
            Or email us directly at{' '}
            <Box component="a" href="mailto:info@rewarding-app.com" sx={{ color: '#ff9e00', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              info@rewarding-app.com
            </Box>
          </Typography>
        </Box>
      </Container>
    </PageBg>
  );
}
